import type { Mitgelieferte, Recht, Rolle } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Welche Rolle welchen Weg sieht — und was davon am Telefon gilt.

   **Eine Tabelle, drei Leser.**

   Luca, 16.09.2026: *„Die Mobile-App-Ansichten für die normalen
   Mitarbeiter — kaufmännischer oder technischer Außendienst —, sodass
   sie nur sehen, was sie mit ihrer Tagesmappe zu tun haben."*

   Bis dahin standen in `Leiste.tsx` bei fast jedem Punkt `rollen: []` —
   „jeder darf". Der technische Außendienst sah damit dasselbe Menü wie
   die Disposition: Brett, Stammdaten, Angebote, Preisstamm. Dass er
   nichts davon anfassen sollte, stand in
   [17-planungsschnitt.md](../../../../docs/17-planungsschnitt.md) und
   sonst nirgends.

   Diese Datei ist die eine Stelle, an der es jetzt steht. Sie wird
   dreimal gelesen:

   1. **Die Seitenleiste** zeigt nur die Punkte, die zur Rolle gehören.
      Kein Ausgrauen — was nicht erlaubt ist, erscheint nicht
      (28-designkonzept.md).
   2. **Der Wegwächter** im Routenbaum (`router.tsx`) schickt auf den
      eigenen Einstieg zurück, wenn jemand die Adresse tippt.
   3. Niemand sonst. **Der Riegel ist die API** (`apps/api/src/mitte/rechte.ts`).
      Eine Maske, die einen Punkt ausblendet, hat niemanden daran
      gehindert, die Route aufzurufen — diese Datei ist Wegweisung, kein
      Schloss.

   **Disposition, Buchhaltung und Verwaltung sehen, was sie heute sehen.**
   Enger geworden ist nur, was neu ist: `technik` und `aussendienst`.

   ── Seit dem 18.09.2026 nennt jeder Weg ein RECHT ──────────────────

   Luca: *„Wir machen Start heute, und da hat ein Außendienst-Kranfahrer
   Start nicht als Recht. Da musst du in der Rechteverwaltung noch mal
   ein bisschen granularer machen."*

   Die Rolle war die falsche Einheit. Sie bringt ein Dutzend Wege auf
   einmal mit, und wer zwei Rollen trägt — im kleinen Betrieb der
   Normalfall —, bekommt zwangsläufig zu viel: Der Kranfahrer braucht
   `aussendienst` für Kundenakte und Preisauskunft und hat damit „Start".
   Ihm die Rolle zu nehmen, nähme ihm auch den Kunden.

   Jetzt nennt jeder Weg ein **Recht**, und eine Rolle **bündelt**
   Rechte (`RECHTE_JE_ROLLE`, dieselbe Tabelle wie in `packages/auth` und
   in `role_permission`). Verstellbar ist beides in Einstellungen →
   Benutzer; hier steht nur, woran ein Weg hängt.

   **Niemand sieht dadurch weniger:** Die Bündel unten sind die wörtliche
   Übersetzung der Rollenlisten, die bis zum 18.09.2026 hier standen.

   **Eine Ausnahme ist Absicht:** Die Tagesmappe (`/heute`, bis zum
   19.09.2026 `/handy`) verlangt jetzt `tagesmappe` und damit nicht mehr
   den kaufmännischen Außendienst. Er hatte dort
   ohnehin kein Fach („Heute" gibt es nur mit Tagesmappe,
   `handywege.ts`), sein Einstieg ist seit dem 17.09.2026 `/start`, und
   ein Weg, den man nur durch Tippen der Adresse findet, ist keiner.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Was eine Rolle mitbringt — **solange niemand es verstellt hat**.
 *
 * Der Rückfall für eine Sitzung, die ihre gerechneten Rechte noch nicht
 * mitbringt (`Ich.rechte`). Dieselben Zeilen wie `RECHTE_JE_ROLLE` in
 * `packages/auth`; dort ist die Quelle, hier die Abschrift für den Browser.
 * Läuft beides auseinander, gewinnt die API — sie ist der Riegel.
 *
 * **Nur die fünf mitgelieferten stehen hier** (Migration 0150). Eine Rolle,
 * die der Betrieb angelegt hat, bündelt nur, was in `role_permission` steht —
 * im Code kann über sie nichts stehen. Sie fällt hier deshalb auf „bringt
 * nichts mit", und das ist die richtige Antwort: Die Wegweisung ist dann eine
 * Rolle zu eng, bis `Ich.rechte` da ist. Der Riegel der API ist es nie.
 */
export const RECHTE_JE_ROLLE: Record<Mitgelieferte, Recht[]> = {
  technik: ["tagesmappe"],
  aussendienst: ["start", "planung", "kunden", "preise", "angebote", "aufnahmen"],
  disposition: [
    "tagesmappe", "start", "planung", "disponieren", "kunden", "preise",
    "angebote", "vermieten", "aufnahmen", "stammdaten",
  ],
  buchhaltung: [
    "start", "planung", "kunden", "preise", "angebote", "vermieten",
    "aufnahmen", "stammdaten", "abrechnung",
  ],
  verwaltung: [
    "tagesmappe", "start", "planung", "disponieren", "kunden", "preise",
    "angebote", "vermieten", "aufnahmen", "stammdaten", "abrechnung", "verwalten",
    "pruefen",
  ],
  /* Der Prüfer (0274, B7): Aufträge und Belege lesen, Beträge sehen, Archiv
     und Export. Schreiben weist die API ab (`mitte/nurlesen.ts`). */
  pruefer: ["preise", "angebote", "pruefen"],
};

/**
 * Die Rechte dieser Rollen — **oder die der Sitzung**, wenn sie feststehen.
 *
 * Der Entzug an einem einzelnen Menschen (`staff_permission`) lässt sich aus
 * Rollen nicht ableiten. Reicht die Sitzung ihre gerechneten Rechte durch
 * (`rechteMerken`), gelten die; sonst die Bündel. Die API weiß es in jedem
 * Fall — sie ist der Riegel, das hier ist die Wegweisung.
 */
let gemerkteRechte: Recht[] | null = null;

/** Was diese eine Rolle im Code mitbringt — leer bei einer eigenen Rolle (0150). */
function buendelImCode(rolle: Rolle): Recht[] {
  return (RECHTE_JE_ROLLE as Record<string, Recht[] | undefined>)[rolle] ?? [];
}

export function rechteAus(rollen: Rolle[]): Recht[] {
  if (gemerkteRechte !== null) return gemerkteRechte;
  const menge = new Set<Recht>();
  for (const rolle of rollen) for (const recht of buendelImCode(rolle)) menge.add(recht);
  return [...menge];
}

/** Hat jemand mit diesen Rollen eines dieser Rechte? */
function darf(rollen: Rolle[], ...verlangt: Recht[]): boolean {
  const hat = rechteAus(rollen);
  return verlangt.some((r) => hat.includes(r));
}

/** Jeder, der überhaupt etwas darf — für „Mehr" und die Stempeluhr. */
const JEDER: Recht[] = [
  "tagesmappe", "start", "planung", "disponieren", "kunden", "preise",
  "angebote", "vermieten", "aufnahmen", "stammdaten", "abrechnung", "verwalten",
  "pruefen",
];

/**
 * Welches Recht welcher Weg verlangt. Eine leere Liste gibt es hier nicht:
 * Jeder Weg nennt sein Recht, damit ein neuer Weg auffällt, statt still
 * überall mitzukommen.
 */
export const WEGRECHTE: Record<string, Recht[]> = {
  /* Die Tagesmappe — **der einzige Weg des technischen Außendiensts**. Es
     ist ein Schirm, kein Umschalter: Was darauf steht, entscheidet die
     Rolle (`masken/Handy.tsx`).

     Seit dem 18.09.2026 hängt sie am Recht `tagesmappe` und damit nicht
     mehr am kaufmännischen Außendienst. Er hatte hier ohnehin kein Fach,
     und sein Einstieg ist `/start`.

     **Seit dem 19.09.2026 heißt sie `/heute` und nicht mehr `/handy`**
     (`schale/altadressen.ts`), und der Einsatz liegt unter `/einsatz` statt
     darunter. Er verlangt dasselbe Recht wie vorher, als er noch unter
     `/handy` fiel: die Tagesmappe. Die alten Adressen stehen hier nicht
     mehr — sie werden übersetzt, bevor der Wächter fragt, und gefragt wird
     dann das Recht des neuen Wegs. */
  /* **`/heute` ist seit dem 21.09.2026 das eine Modul** — der Tag UND das,
     was vorher auf `/start` stand. Es verlangt deshalb eines von beiden
     Rechten; welche Abschnitte jemand darin sieht, entscheidet die Maske je
     Recht (`masken/HeuteTelefon.tsx`, `rollenMitRecht`). */
  "/heute": ["tagesmappe", "start"],
  "/einsatz": ["tagesmappe"],

  /* Das Nachschlagen und der Preis — ein Blatt, zwei Einstiege
     (`masken/NachschlagenTelefon.tsx`). An jedem hängt ein Preis, und das
     ist genau die Spalte „sieht nie" des Technikers (17-planungsschnitt.md).
     „Mehr" gehört **allen**, die überhaupt etwas dürfen: Dort steht das
     Abmelden. */
  "/nachschlagen": ["preise"],
  "/preis": ["preise"],
  "/mehr": JEDER,
  /* Das Nachfassen hängt an denselben Aufnahmen wie die Begehung — es ist
     die Arbeit des kaufmännischen Außendiensts zwischen zwei Terminen. */
  "/nachfassen": ["aufnahmen"],

  /* Die Altadresse — sie leitet auf `/heute` (`router.tsx`). Ihr Recht
     bleibt stehen, damit ein Lesezeichen nicht am Wächter scheitert, bevor
     die Umleitung greift. */
  "/start": ["start", "tagesmappe"],
  /* Das Brett. Der kaufmännische Außendienst sieht es **lesend** — die
     Verfügbarkeitsprüfung steht in seiner Zeile in 17-planungsschnitt.md.
     Geschaltet wird an der API (`disponieren`), nicht hier. */
  "/planung": ["planung"],
  /* **Disposition** (08.10.2026, docs/65): Aus der Planung sind zwei Punkte
     geworden. Der Kalender ist das Brett und hängt am selben Recht; `/planung`
     leitet dorthin und behält sein Recht, damit ein Lesezeichen nicht am
     Wächter scheitert. Einteilen ist die Arbeit der Disposition — dieselben
     Rollen wie `GET /verteilen`. */
  "/kalender": ["planung"],
  "/einteilen": ["disponieren"],
  /* Verteilen (24.09.2026, M8): wer macht was — Arbeit der Disposition.
     Seit dem 25.09.2026 keine eigene Seite mehr, sondern eine Weiterleitung
     ins Brett (`router.tsx`, `zumVerteilen`). Das Recht bleibt: Die API
     verlangt dieselben Rollen (`routen/vorbereitung.ts`). */
  "/verteilen": ["disponieren"],
  "/vermieten": ["vermieten"],
  "/angebote": ["angebote"],
  "/auftraege": ["angebote"],
  "/aufnahmen": ["aufnahmen"],

  "/kunden": ["kunden"],
  /* Die Suche am Telefon (19.09.2026) — das Fach, in dem „Kunden"
     aufgegangen ist. Sie hängt am selben Recht; welche Sorten darin
     erscheinen, entscheidet sie je Sorte selbst (`masken/SucheTelefon.tsx`). */
  "/suche": ["kunden"],
  /* Stammdaten pflegt der Innendienst. Der Außendienst nennt einen Preis
     aus der Preisauskunft und pflegt keinen Tarif — 17-planungsschnitt.md,
     „sieht nie: Marge, Kostensätze". */
  "/ressourcen": ["stammdaten"],
  "/preise": ["stammdaten"],
  "/weiteres": ["stammdaten"],

  "/abrechnung": ["abrechnung"],
  /* Die Belegsuche (18.09.2026): dieselbe Zeile wie die Aufträge. Der
     Außendienst sucht „die AB, die wir Kramer geschickt haben", die
     Buchhaltung die offene Rechnung. Der Techniker sieht keine Preise
     (17-planungsschnitt.md); die API sperrt ihn ebenso
     (`routen/belege.ts`). */
  "/belege": ["angebote"],
  /* Der Prüfer (0274, B7) kommt auch hinein — und sieht dort nur das Archiv
     (GoBD): Welche Seiten jemand sieht, entscheidet `seiten.ts` je Recht. */
  "/einstellungen": ["verwalten", "pruefen"],
  /* **`/handbuch` steht hier nicht, und das ist Absicht** (21.09.2026). Ein
     Weg ohne Eintrag ist offen (`darfWeg`) — genau das soll das Handbuch
     sein: Es erklärt die Anwendung, und wer es am meisten braucht, hat die
     wenigsten Rechte. Auch `JEDER` wäre zu eng: Wer gar kein Recht hat,
     stünde dann vor einer Umleitung auf einen Schirm, der ihm nichts sagt,
     statt vor der Erklärung, was das hier ist. */
};

/**
 * Die Rollen zu einem Navigationspunkt — für `Leiste.tsx`.
 *
 * Die Tabelle nennt seit dem 18.09.2026 Rechte; `Leiste.tsx` fragt aber
 * weiter nach Rollen. Übersetzt wird hier und nicht dort: **Jede Rolle, die
 * eines der verlangten Rechte bündelt.** Was ein einzelner Mensch darüber
 * hinaus hat oder verloren hat, steht in `darfWeg` — dort wird entschieden,
 * hier nur beschriftet.
 */
export function rollenFuer(weg: string): Rolle[] {
  const verlangt = WEGRECHTE[weg];
  if (!verlangt) return [];
  return rollenMitRecht(...verlangt);
}

/**
 * Die Rollen, die eines dieser Rechte bündeln — für Masken, die **in sich**
 * je Recht anders aussehen (21.09.2026).
 *
 * Luca: *„Manchmal wird ein Modul, und dann, je nach Rolle, verändert sich
 * das Modul."* „Heute" ist so eines: Der Tag hängt an `tagesmappe`, das
 * Anfangen und die Arbeit an `start`, und wer beides hat, sieht beides auf
 * einem Schirm. Dafür braucht die Maske die Rollen zu einem **Recht** und
 * nicht zu einem Weg — den Weg gibt es ja nur noch einmal.
 */
export function rollenMitRecht(...verlangt: Recht[]): Rolle[] {
  return (Object.keys(RECHTE_JE_ROLLE) as Rolle[])
    .filter((rolle) => buendelImCode(rolle).some((r) => verlangt.includes(r)));
}

/**
 * Der längste Eintrag der Tabelle, der auf diesen Weg passt.
 *
 * Verglichen wird auf den **Anfang**: `/kunden/K-10001` gehört zu
 * `/kunden`, `/einsatz/…/uebergabe` zu `/einsatz`. Der längste passende
 * Anfang gewinnt — sonst entschiede die Reihenfolge der Tabelle, und ein
 * genauerer Eintrag unter einem kürzeren (so stand bis zum 19.09.2026
 * `/handy/preis` unter `/handy`) bekäme das Recht des kürzeren.
 */
function eintragZu(pfad: string): Recht[] | null {
  let treffer: Recht[] | null = null;
  let laenge = -1;
  for (const [weg, erlaubt] of Object.entries(WEGRECHTE)) {
    if ((pfad === weg || pfad.startsWith(`${weg}/`)) && weg.length > laenge) {
      treffer = erlaubt;
      laenge = weg.length;
    }
  }
  return treffer;
}

/**
 * Darf dieser Mensch auf diesen Weg?
 *
 * Ein Weg, der in der Tabelle nicht steht, ist offen. Das ist Absicht: Eine
 * neue Maske soll nicht dadurch unerreichbar werden, dass jemand hier eine
 * Zeile vergessen hat. Was schützenswert ist, steht in der API.
 */
export function darfWeg(rollen: Rolle[], pfad: string): boolean {
  const erlaubt = eintragZu(pfad);
  return erlaubt === null || darf(rollen, ...erlaubt);
}

/**
 * **Dieselbe Frage mit den Rechten des Menschen statt seiner Rollen**
 * (Durchgang 03.10.2026, A-17).
 *
 * Die Leiste und einige Masken fragten bis dahin `darf(rollenFuer(weg))` —
 * also „trägt er eine Rolle, deren Bündel das Recht hat". Ein Recht, das
 * einem einzelnen Menschen dazugegeben oder entzogen war (`staff_permission`,
 * 0147), sah die Oberfläche damit nie: Pia behielt „Kunden" in der Leiste,
 * obwohl die API 403 sagte, und bekam „Abrechnung" nicht, obwohl sie durfte.
 * `rechte` kommt aus `GET /ich` und ist vom Server gerechnet (`schale/Sitzung.tsx`).
 */
export function wegErlaubt(rechte: readonly Recht[], pfad: string): boolean {
  const erlaubt = eintragZu(pfad);
  return erlaubt === null || erlaubt.some((r) => rechte.includes(r));
}

/** Die Bündel dieser Rollen, nur aus dem Code — der Rückfall, wenn `ich.rechte` fehlt (ältere API). */
export function buendelAus(rollen: Rolle[]): Recht[] {
  const menge = new Set<Recht>();
  for (const rolle of rollen) for (const recht of buendelImCode(rolle)) menge.add(recht);
  return [...menge];
}

/**
 * Wo dieser Mensch nach der Anmeldung landet.
 *
 * **Der technische Außendienst immer am Telefonschirm**, auch am großen
 * Bildschirm: Er hat sonst nichts, und eine Startseite mit vier leeren
 * Karten wäre eine Maske, die ihm etwas verspricht.
 *
 * **Wer gar keine Rolle hat, ebenfalls.** Dort steht ein freundlicher
 * leerer Schirm; die Startseite dagegen fragt die API nach Zahlen, die er
 * nicht bekommt, und zeigte ihm einen Fehler statt einer Auskunft.
 */
export function einstieg(_rollen: Rolle[], _amTelefon: boolean): string {
  /* **Seit dem 21.09.2026 gibt es nur noch einen Einstieg** (Luca: *„Wie kann
     man heute und Start zusammenbringen?"*). `/heute` ist das Modul, und was
     darin steht, entscheiden die Rechte — der Techniker sieht seinen Tag, der
     Verkauf sein Anfangen, die Disposition beides.

     Vorher standen hier drei Fälle: keine Rolle → `/heute`, kein `start` →
     `/heute`, am Telefon mit Tagesmappe → `/heute`, sonst `/start`. Alle vier
     Wege enden jetzt an derselben Stelle; die Verzweigung ist in die Maske
     gewandert, wo sie hingehört. */
  return "/heute";
}

/* ══════════════ `MOBILWEG` ist am 17.09.2026 entfallen ══════════════

   Die Tabelle war die **Notlösung** aus der Zeit, in der eine große Maske
   am Telefon auf ihr `/handy/…`-Doppel umgeleitet wurde. Luca,
   16.09.2026: *„Das muss ja theoretisch automatisch erkannt werden, wenn
   man auf dem Handy unterwegs ist."* — und am 17.09.2026: *„Das muss ja
   keine extra URL sein, und die Module müssen alle mobile-fähig sein."*

   Am Abend des 17.09.2026 zeigte **kein** Eintrag mehr auf etwas:
   `/kunden` und `/preise` griffen nie (`TELEFONTAUGLICH` wird vorher
   gefragt), `/start`, `/planung`, `/angebote` und `/aufnahmen` sind mit
   ihren Telefonfassungen herausgefallen, und der letzte Eintrag
   (`/ressourcen` → `/handy/nachschlagen`) war seit dem Gerätestamm in
   `TELEFONTAUGLICH` tot — er hätte den Maschinenstamm auf eine
   Preisauskunft geschickt. Also sind Tabelle und `mobilFassung` weg
   (40-mobiles-grundgeruest.md, Schritt 4: „Ist `MOBILWEG` leer, fällt es
   weg").

   **Der Rückfall auf den Einstieg bleibt**, siehe `wegZiel`: Er ist jetzt
   die Antwort auf eine Adresse, die es nicht gibt, nicht mehr die
   Umleitung einer Maske. */

/* ══════════════ Wege, die am Telefon selbst tragen ══════════════

   Luca, 17.09.2026: *„Das muss ja keine extra URL sein, und die Module
   müssen alle mobile-fähig sein."*

   Die Umleitung oben ist die **Notlösung** und nicht das Ziel: Sie
   schickt weg, was am Telefon nicht zu bedienen wäre, weil eine
   geschrumpfte Schreibtischmaske schlimmer ist als keine
   ([09-mobile.md](../../../../docs/09-mobile.md)). Ein Modul, das für
   den Finger gebaut ist, braucht sie nicht mehr — es trägt sich selbst.

   **Wer ein Modul telefontauglich macht, trägt seinen Weg hier ein**,
   und der Wegwächter lässt ihn danach stehen. Das ist die eine Zeile,
   an der man sieht, wie weit die Anwendung damit ist; ohne sie stünde
   es in jeder Maske einzeln und nirgends zusammen.

   **Die Arbeit draußen steht seit dem 19.09.2026 darin wie jeder andere
   Weg** (`/heute`, `/einsatz`, `/nachfassen`). Bis dahin lag sie unter
   `/handy…` und wurde vorher pauschal durchgelassen — eine Ausnahme, die an
   der Adresse hing. Die drei Blattwege stehen ebenfalls darin: Sie öffnen
   ein Blatt über der Maske, und ohne Eintrag schickte der Wächter sie am
   Telefon auf den Einstieg, statt sie das Blatt öffnen zu lassen.

   **Ein Weg kommt herein, wenn jemand ihn auf 390 px durchgesehen hat** —
   jedes Blatt, hell und dunkel, ohne waagerechtes Rollen und mit Zielen,
   die ein Daumen trifft. Die Einträge unten sind am 17.09.2026 so geprüft
   worden.

   **`/angebote` steht seit dem 17.09.2026 darin.** Bis dahin führte der
   Weg am Telefon auf `/handy/angebot`, weil die Liste den kurzen Weg nicht
   hatte. Jetzt zeichnet `/angebote/neu` am Telefon selbst den Ablauf in
   vier Griffen (`masken/vorgaenge/AngebotNeu.tsx`, 40-mobiles-grundgeruest.md,
   Frage 5) — der Außendienst verliert den kurzen Weg nicht, er bekommt
   die Liste und das einzelne Angebot dazu.

   **`/planung` steht seit dem 17.09.2026 darin** — und die Begründung, die
   hier bis dahin stand, gilt weiter: Ein Belegungsband auf 390 px ist
   Raten statt Sehen. Luca: *„Ich will alles vollständig mobile-optimiert
   haben."* Also zeichnet `/planung` am Telefon kein kleineres Brett,
   sondern eine **Agenda** (`masken/planung/Agenda.tsx`): Tag oder Woche,
   je Ressource die Belegungen als Karten mit Art als Form und Wort,
   Konflikte, „frei ab", und Umplanen über dasselbe Blatt wie am
   Schreibtisch — ohne Ziehen. Maschine und Mensch in einer Liste
   (Regel 8). Am Tablet bleibt das Brett, bedienbar mit dem Finger. */
const TELEFONTAUGLICH: string[] = [
  /* **`/planung` ist am 18.09.2026 herausgefallen.** Die Route leitet am
     Telefon selbst auf `/start` um; der Eintrag hier hätte also nichts mehr
     zu tun — er behauptete nur weiter, das Brett trage sich auf 390 px.
     Eine Zeile, die nichts bewirkt und etwas Falsches sagt, ist die
     schlechtere von beiden. */
  // Die Startseite seit dem 17.09.2026: Am Telefon zeichnet sie den Schirm
  // der Rolle (`masken/StartTelefon.tsx`) — Wiedervorlagen, Nachschlagen,
  // Notiz, zuletzt besucht und die Karten des Schreibtischs als Abschnitte.
  // `/handy/start` leitet dauerhaft hierher (`schale/altadressen.ts`).
  "/start",
  // Vermieten seit dem 17.09.2026: die vier Fragen untereinander, jeder
  // Treffer eine Karte mit Tagesstreifen und großem „Merken", die Sammlung
  // als Blatt von unten, der Abschluss füllt den Schirm.
  "/vermieten",
  // Die Ortsaufnahmen seit dem 17.09.2026: meine, der Pool, erledigt und der
  // Auszug „Nachfassen" (`masken/vorgaenge/AufnahmenTelefon.tsx`).
  // `/handy/aufnahmen` und `/handy/aufgaben` leiten dauerhaft hierher.
  // Darunter seit dem 19.09.2026 die Begehung selbst (`/aufnahmen/$id`).
  "/aufnahmen",
  // Kundenliste, Akte und „Kunde anlegen" — Formulare, Personenblatt,
  // Ortsformular und Anschrift einzeln nachgesehen. Seit dem 17.09.2026
  // zeigt `/kunden/$nr` am Telefon zuerst die kurze Akte fürs Auto und die
  // übrigen Teile als Reiter; `/handy/kunden` leitet hierher um.
  "/kunden",
  // Die Suche (19.09.2026): am Telefon eine Wurzel mit eigenem Fach, am
  // Schreibtisch das Suchfenster über der Maske (`router.tsx`).
  "/suche",
  // Angebotsliste, das einzelne Angebot mit seinem Blatt, und unter
  // `/angebote/neu` der Ablauf in vier Griffen (17.09.2026).
  "/angebote",
  // Stammdaten und ihre Nachbarn. Der Katalog ist am Telefon eine
  // rollende Leiste statt einer Spalte (`masken/stamm/katalogrolle.ts`).
  "/preise",
  "/weiteres",
  // Die Ressourcen seit dem 17.09.2026 (abends): Karten statt Tafel,
  // Reiter und Pillen als rollende Reihe, der Kopf mit „Maschine anlegen"
  // in einer Zeile, und die Blätter der vier Reiter füllen den Schirm.
  // Nachgesehen auf 390 px, hell und dunkel, jeder Reiter mit seinem Blatt.
  "/ressourcen",
  "/abrechnung",
  // Die Belegsuche (18.09.2026): Filter untereinander, jede Zeile eine
  // Karte, das Blatt füllt den Schirm — dieselbe Form wie die Aufträge.
  "/belege",
  "/einstellungen",
  /* Das Handbuch (21.09.2026): eine Spalte, das Inhaltsverzeichnis
     zugeklappt darüber, jede Tabelle in ihrem eigenen Rollbereich und die
     Zeichnungen in Festschrift. Auf 390 px durchgesehen — kein waagerechtes
     Rollen der Seite, und der Grund, es überhaupt mitzubauen, gilt gerade
     am Telefon: Wer draußen nachsieht, hat kein Netz. */
  "/handbuch",
  // Mein Konto (E4, 04.10.2026): zwei Bögen untereinander, das Bild für die App 184 px.
  "/konto",
  // Auftragsliste und Auftragsblatt. Die Seitenspalte des Blattes war
  // unter 1100 px ausgeblendet — Phase, Frist, Netto und „Kunde hat
  // angenommen" waren am Telefon unerreichbar.
  "/auftraege",
  // Die Arbeit draußen (19.09.2026, vorher unter `/handy…`): für den Finger
  // gebaut, von Anfang an. Die Tagesmappe, der Einsatz mit seinen Schritten
  // und das Nachfassen.
  "/heute",
  "/einsatz",
  /* Verteilen (25.09.2026): keine Seite mehr, eine Weiterleitung — am
     Telefon auf die Startseite, wo der Abschnitt „Zu verteilen" steht
     (`masken/handy/Zuverteilen.tsx`). Sie steht hier, damit der Wächter sie
     nicht vorher abfängt. */
  "/verteilen",
  "/nachfassen",
  // Die drei Blattwege — sie öffnen ihr Blatt und führen zurück (`router.tsx`).
  "/nachschlagen",
  "/preis",
  "/mehr",
];

function traegtSichSelbst(pfad: string): boolean {
  return TELEFONTAUGLICH.some((weg) => pfad === weg || pfad.startsWith(`${weg}/`));
}

/* ══════════════ „Große Ansicht" ist am 20.09.2026 entfallen ══════════════

   Sie stand in „Mehr" und merkte sich im `sessionStorage`, dass jemand am
   Telefon die Schreibtischfassung sehen will. Seit dem 17.09. schaltete sie
   nichts mehr: Jede Maske trägt sich am Telefon selbst (`TELEFONTAUGLICH`),
   der Wegwächter schickt also niemanden mehr heim, und `/planung` fragte
   den Schalter nie. Luca: *„Große Ansicht rausnehmen, wenn das nichts
   bringt."* Eine Einstellung, die nichts tut, ist schlimmer als keine.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Nur die erste der beiden Fragen: Darf diese Rolle hierher?
 *
 * **Für den Lagewechsel** (40-mobiles-grundgeruest.md, Schritt 3). Wer ein
 * Fenster schmal zieht oder das Telefon dreht, hat nichts aufgerufen — er
 * bekommt dieselbe Adresse neu gezeichnet und nicht eine andere. Gemessen
 * am 17.09.2026: 1280 → 600 → 1280 px machte aus `/angebote` dauerhaft
 * `/handy/angebot`, und der Ausgangspunkt war weg. Das Recht hängt nicht
 * an der Lage und bleibt deshalb die eine Frage, die auch dann gilt.
 */
export function rechtZiel(
  rollen: Rolle[] | null,
  pfad: string,
  amTelefon: boolean,
): string | null {
  if (rollen === null) return null;
  const heim = einstieg(rollen, amTelefon);
  if (pfad === "/") return heim;
  // Der eigene Einstieg ist immer erlaubt — sonst liefe ein Mensch ohne
  // Rolle im Kreis: weggeschickt auf einen Weg, den er auch nicht darf.
  const istHeim = pfad === heim || pfad.startsWith(`${heim}/`);
  if (!istHeim && !darfWeg(rollen, pfad)) return heim;
  return null;
}

/**
 * Wohin der Wegwächter schickt — oder `null`, wenn der Weg bleiben darf.
 *
 * Zwei Fragen in dieser Reihenfolge:
 *
 * 1. **Darf er hin?** Wenn nicht, geht es auf seinen Einstieg. Das gilt am
 *    Telefon wie am Schirm.
 * 2. **Ist er am Telefon, und trägt der Weg sich dort?** Wenn nicht, geht es
 *    ebenfalls auf den Einstieg.
 *
 * **Seit dem 17.09.2026 trifft die zweite Frage keine gebaute Maske mehr:**
 * Jede Route des Routenbaums steht in `TELEFONTAUGLICH` (seit dem 19.09.2026
 * auch die Arbeit draußen, die vorher unter `/handy/…` pauschal durchging).
 * Übrig ist damit der Rückfall für eine Adresse, die es nicht
 * gibt — eine getippte, ein altes Lesezeichen. `TELEFONTAUGLICH` bleibt
 * trotzdem stehen: Es ist die Liste, an der eine **neue** Maske sagt, dass
 * jemand sie am Telefon durchgesehen hat, und ohne sie bekäme eine
 * ungeprüfte Maske stillschweigend 390 px.
 *
 * `rollen === null` heißt: Die Sitzung steht noch nicht fest. Dann wird
 * **nicht** umgeleitet; sonst schöbe der erste Bildaufbau jeden auf einen
 * Einstieg, bevor überhaupt bekannt ist, wer er ist.
 */
export function wegZiel(
  rollen: Rolle[] | null,
  pfad: string,
  amTelefon: boolean,
): string | null {
  if (rollen === null) return null;
  const recht = rechtZiel(rollen, pfad, amTelefon);
  if (recht !== null) return recht;
  const heim = einstieg(rollen, amTelefon);

  if (!amTelefon) return null;
  // Was sich am Telefon selbst trägt, bleibt, wo es ist (`TELEFONTAUGLICH`).
  if (traegtSichSelbst(pfad)) return null;
  return heim;
}

/* ══════════════ Die Rollen, wie der Router sie liest ══════════════

   Der Wegwächter sitzt im Routenbaum und nicht in einer Komponente — er
   muss laufen, **bevor** eine Maske lädt. Dort gibt es keinen React-Kontext,
   also liegt die Rolle daneben: `SitzungsRaum` schreibt sie hinein, sobald
   sie feststeht, `main.tsx` lässt den Router danach neu prüfen.

   `null` ist nicht dasselbe wie „keine Rolle": Es heißt „noch unbekannt". */

let gemerkt: Rolle[] | null = null;
const horcher = new Set<() => void>();

function benachrichtigen(): void {
  for (const h of horcher) h();
}

export function rollenMerken(neu: Rolle[] | null): void {
  /* Dieselben Rollen noch einmal sind kein Wechsel. `SitzungsRaum` meldet
     bei jedem neuen `ich` — auch nach einer Profiländerung —, und ein
     Wechsel lässt den Wegwächter wieder nach der Lage fragen. Wer am
     schmal gezogenen Fenster auf `/angebote` steht, soll dort nicht
     weggeschickt werden, weil sich sein Name geändert hat. */
  const gleich = neu !== null && gemerkt !== null
    && neu.length === gemerkt.length && neu.every((r) => gemerkt?.includes(r));
  if (gleich || neu === gemerkt) return;
  gemerkt = neu;
  benachrichtigen();
}

/**
 * Die **gerechneten** Rechte der Sitzung — was `GET /ich` unter `rechte`
 * liefert (Migration 0147).
 *
 * Solange sie niemand meldet, rechnet `rechteAus` sie aus den Rollen. Das
 * Ergebnis ist dasselbe, solange kein einzelner Mensch ein Recht dazubekommen
 * oder verloren hat — nur dieser Fall lässt sich aus Rollen nicht ableiten,
 * und genau darum ging es Luca am 18.09.2026.
 *
 * **Eine Zeile fehlt noch am anderen Ende**: `daten/routen.ts` (`alsIch`)
 * muss `rechte` durchreichen und `schale/Sitzung.tsx` sie hier melden. Beide
 * Dateien gehören heute anderen Händen; bis dahin ist der Riegel der API
 * genau, die Wegweisung im Browser eine Rolle breit.
 */
export function rechteMerken(neu: Recht[] | null): void {
  const gleich = neu !== null && gemerkteRechte !== null
    && neu.length === gemerkteRechte.length && neu.every((r) => gemerkteRechte?.includes(r));
  if (gleich || neu === gemerkteRechte) return;
  gemerkteRechte = neu;
  benachrichtigen();
}

/** Was die Sitzung darf — `null`, solange es nur die Rollen gibt. */
export function rechteJetzt(): Recht[] | null {
  return gemerkteRechte;
}

export function rollenJetzt(): Rolle[] | null {
  return gemerkt;
}

/** Wer erfahren will, dass sich Rolle oder Ansicht geändert haben. */
export function aufRollenwechsel(h: () => void): () => void {
  horcher.add(h);
  return () => { horcher.delete(h); };
}
